/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeButton,
  DimeCard,
  DimeCardContent,
  DimeCardDescription,
  DimeCardFooter,
  DimeCardHeader,
  DimeCardTitle,
  DimeField,
  DimeFieldLabel,
  DimeInput,
  DimeSeparator,
  DimeTabCount,
  DimeTabs,
  DimeTabsContent,
  DimeTabsList,
  DimeTabsTrigger,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { BarChart3, Settings, User } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/tabs')({
  component: TabsDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-start justify-center gap-4 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

// ── Examples ───────────────────────────────────────────────────────────────────

function BasicExample() {
  return (
    <DimeTabs defaultValue="account" className="w-[400px]">
      <DimeTabsList>
        <DimeTabsTrigger value="account">Account</DimeTabsTrigger>
        <DimeTabsTrigger value="password">Password</DimeTabsTrigger>
      </DimeTabsList>
      <DimeTabsContent value="account">
        <DimeCard>
          <DimeCardHeader>
            <DimeCardTitle>Account</DimeCardTitle>
            <DimeCardDescription>
              Make changes to your account here. Click save when you're done.
            </DimeCardDescription>
          </DimeCardHeader>
          <DimeCardContent className="space-y-3">
            <DimeField>
              <DimeFieldLabel htmlFor="tab-name">Name</DimeFieldLabel>
              <DimeInput id="tab-name" defaultValue="Devon Park" />
            </DimeField>
            <DimeField>
              <DimeFieldLabel htmlFor="tab-username">Username</DimeFieldLabel>
              <DimeInput id="tab-username" defaultValue="@devon" />
            </DimeField>
          </DimeCardContent>
          <DimeCardFooter>
            <DimeButton size="sm">Save changes</DimeButton>
          </DimeCardFooter>
        </DimeCard>
      </DimeTabsContent>
      <DimeTabsContent value="password">
        <DimeCard>
          <DimeCardHeader>
            <DimeCardTitle>Password</DimeCardTitle>
            <DimeCardDescription>
              Change your password here. After saving, you'll be logged out.
            </DimeCardDescription>
          </DimeCardHeader>
          <DimeCardContent className="space-y-3">
            <DimeField>
              <DimeFieldLabel htmlFor="tab-current">Current password</DimeFieldLabel>
              <DimeInput id="tab-current" type="password" />
            </DimeField>
            <DimeField>
              <DimeFieldLabel htmlFor="tab-new">New password</DimeFieldLabel>
              <DimeInput id="tab-new" type="password" />
            </DimeField>
          </DimeCardContent>
          <DimeCardFooter>
            <DimeButton size="sm">Save password</DimeButton>
          </DimeCardFooter>
        </DimeCard>
      </DimeTabsContent>
    </DimeTabs>
  );
}

function CountExample() {
  return (
    <DimeTabs defaultValue="open" className="w-[400px]">
      <DimeTabsList>
        <DimeTabsTrigger value="open">
          Open <DimeTabCount>5</DimeTabCount>
        </DimeTabsTrigger>
        <DimeTabsTrigger value="late">
          Late <DimeTabCount tone="destructive">2</DimeTabCount>
        </DimeTabsTrigger>
        <DimeTabsTrigger value="review">
          Review <DimeTabCount placement="corner">3</DimeTabCount>
        </DimeTabsTrigger>
      </DimeTabsList>
      {(['open', 'late', 'review'] as const).map((value) => (
        <DimeTabsContent key={value} value={value} className="pt-4">
          <p className="text-sm text-muted-foreground">The {value} items.</p>
        </DimeTabsContent>
      ))}
    </DimeTabs>
  );
}

function LineExample() {
  return (
    <DimeTabs defaultValue="overview" className="w-[400px]">
      <DimeTabsList variant="line">
        <DimeTabsTrigger value="overview">Overview</DimeTabsTrigger>
        <DimeTabsTrigger value="analytics">Analytics</DimeTabsTrigger>
        <DimeTabsTrigger value="reports">Reports</DimeTabsTrigger>
      </DimeTabsList>
      <DimeTabsContent value="overview" className="pt-4">
        <p className="text-sm text-muted-foreground">
          Overview content — summary metrics and recent activity.
        </p>
      </DimeTabsContent>
      <DimeTabsContent value="analytics" className="pt-4">
        <p className="text-sm text-muted-foreground">Analytics content — charts and trends.</p>
      </DimeTabsContent>
      <DimeTabsContent value="reports" className="pt-4">
        <p className="text-sm text-muted-foreground">Reports content — downloadable exports.</p>
      </DimeTabsContent>
    </DimeTabs>
  );
}

function VerticalExample() {
  return (
    <DimeTabs defaultValue="profile" orientation="vertical" className="w-[400px]">
      <DimeTabsList>
        <DimeTabsTrigger value="profile">Profile</DimeTabsTrigger>
        <DimeTabsTrigger value="billing">Billing</DimeTabsTrigger>
        <DimeTabsTrigger value="notifications">Notifications</DimeTabsTrigger>
      </DimeTabsList>
      <DimeTabsContent value="profile" className="px-4">
        <p className="text-sm text-muted-foreground">Manage your profile information.</p>
      </DimeTabsContent>
      <DimeTabsContent value="billing" className="px-4">
        <p className="text-sm text-muted-foreground">View invoices and payment methods.</p>
      </DimeTabsContent>
      <DimeTabsContent value="notifications" className="px-4">
        <p className="text-sm text-muted-foreground">Configure notification preferences.</p>
      </DimeTabsContent>
    </DimeTabs>
  );
}

function DisabledExample() {
  return (
    <DimeTabs defaultValue="active" className="w-[400px]">
      <DimeTabsList>
        <DimeTabsTrigger value="active">Active</DimeTabsTrigger>
        <DimeTabsTrigger value="pending">Pending</DimeTabsTrigger>
        <DimeTabsTrigger value="archived" disabled>
          Archived
        </DimeTabsTrigger>
      </DimeTabsList>
      <DimeTabsContent value="active" className="pt-4">
        <p className="text-sm text-muted-foreground">Active records are shown here.</p>
      </DimeTabsContent>
      <DimeTabsContent value="pending" className="pt-4">
        <p className="text-sm text-muted-foreground">Pending records awaiting approval.</p>
      </DimeTabsContent>
    </DimeTabs>
  );
}

function IconsExample() {
  return (
    <DimeTabs defaultValue="account" className="w-[400px]">
      <DimeTabsList>
        <DimeTabsTrigger value="account">
          <User />
          Account
        </DimeTabsTrigger>
        <DimeTabsTrigger value="analytics">
          <BarChart3 />
          Analytics
        </DimeTabsTrigger>
        <DimeTabsTrigger value="settings">
          <Settings />
          Settings
        </DimeTabsTrigger>
      </DimeTabsList>
      <DimeTabsContent value="account" className="pt-4">
        <p className="text-sm text-muted-foreground">Account settings and preferences.</p>
      </DimeTabsContent>
      <DimeTabsContent value="analytics" className="pt-4">
        <p className="text-sm text-muted-foreground">Usage metrics and analytics.</p>
      </DimeTabsContent>
      <DimeTabsContent value="settings" className="pt-4">
        <p className="text-sm text-muted-foreground">Application configuration.</p>
      </DimeTabsContent>
    </DimeTabs>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

function TabsDocs() {
  return (
    <div className="max-w-3xl">
      <h1 className="mb-1 text-3xl font-bold">Tabs</h1>
      <p className="mb-8 text-muted-foreground">
        Layered content panels displayed one at a time. Built on Radix UI. Compose{' '}
        <code className="font-mono text-sm">DimeTabsList</code> +{' '}
        <code className="font-mono text-sm">DimeTabsTrigger</code> +{' '}
        <code className="font-mono text-sm">DimeTabsContent</code> inside{' '}
        <code className="font-mono text-sm">DimeTabs</code>.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <BasicExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">defaultValue</code> to set the initially active tab. Each{' '}
          <code className="font-mono">DimeTabsTrigger</code> and{' '}
          <code className="font-mono">DimeTabsContent</code> share a{' '}
          <code className="font-mono">value</code> string.
        </p>
      </DocsSection>

      <DocsSection title="Line">
        <Preview>
          <LineExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">variant="line"</code> to{' '}
          <code className="font-mono">DimeTabsList</code> for an underline style instead of the
          default pill.
        </p>
      </DocsSection>

      <DocsSection title="Counts">
        <Preview>
          <CountExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeTabCount</code> takes a{' '}
          <code className="font-mono">tone</code> (<code className="font-mono">muted</code> or{' '}
          <code className="font-mono">destructive</code>) and a{' '}
          <code className="font-mono">placement</code> (<code className="font-mono">inline</code> or{' '}
          <code className="font-mono">corner</code>).
        </p>
      </DocsSection>

      <DocsSection title="Vertical">
        <Preview>
          <VerticalExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">orientation="vertical"</code> to{' '}
          <code className="font-mono">DimeTabs</code> to stack the tab list on the left.
        </p>
      </DocsSection>

      <DocsSection title="Disabled">
        <Preview>
          <DisabledExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">disabled</code> to a{' '}
          <code className="font-mono">DimeTabsTrigger</code> to make it non-interactive.
        </p>
      </DocsSection>

      <DocsSection title="Icons">
        <Preview>
          <IconsExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Place an icon before the label inside <code className="font-mono">DimeTabsTrigger</code>.
          Icons inherit the trigger's active and muted colour states automatically.
        </p>
      </DocsSection>
    </div>
  );
}
